import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api/client";
import type {
  DeviceInventoryEntry,
  DiagnosticsBroadcastResponse,
  FirmwareRelease,
  OtaPolicy,
} from "../api/types";

const CHANNEL = "driver-console";
const REFRESH_MS = 15_000;

function fmtBytes(n?: number): string {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function fmtHeartbeat(unixSeconds?: number): { text: string; tone: "ok" | "warn" | "crit" | "info" } {
  if (!unixSeconds) return { text: "never", tone: "info" };
  const ageSec = Math.max(0, Math.floor(Date.now() / 1000 - unixSeconds));
  let tone: "ok" | "warn" | "crit" = "ok";
  if (ageSec > 600) tone = "crit";
  else if (ageSec > 120) tone = "warn";
  if (ageSec < 60) return { text: `${ageSec}s ago`, tone };
  if (ageSec < 3600) return { text: `${Math.round(ageSec / 60)}m ago`, tone };
  if (ageSec < 86400) return { text: `${Math.round(ageSec / 3600)}h ago`, tone };
  return { text: `${Math.round(ageSec / 86400)}d ago`, tone };
}

// Extracts the numeric versionCode appended by the device, e.g. "1.0.4 (10004)" → 10004.
function parseVersionCode(appVersion?: string): number | null {
  if (!appVersion) return null;
  const m = appVersion.match(/\((\d+)\)\s*$/);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : null;
}

interface DeviceRowStatus {
  pill: "ok" | "warn" | "crit" | "info";
  label: string;
}

function deviceVersionStatus(entry: DeviceInventoryEntry, latest: FirmwareRelease | null): DeviceRowStatus {
  if (!entry.app_version) return { pill: "info", label: "unknown" };
  if (!latest) return { pill: "info", label: "no catalog" };
  const code = parseVersionCode(entry.app_version);
  if (code == null) return { pill: "info", label: "no code" };
  if (code === latest.version_code) return { pill: "ok", label: "up to date" };
  if (code > latest.version_code) return { pill: "info", label: "ahead" };
  return { pill: "warn", label: "behind" };
}

/** "Stable" = the version actually rolled out (real OTA policy pin, or the
 *  catalog max when nothing is pinned) — not a fabricated tag. "Beta" is
 *  read off the real version_name when it carries an rc/beta/alpha marker;
 *  everything else is "Legacy". */
function releaseTier(r: FirmwareRelease, rolledOutCode: number | null): "stable" | "beta" | "legacy" {
  if (rolledOutCode != null && r.version_code === rolledOutCode) return "stable";
  if (/rc|beta|alpha/i.test(r.version_name)) return "beta";
  return "legacy";
}

export default function FirmwarePage() {
  const [releases, setReleases] = useState<FirmwareRelease[]>([]);
  const [latest, setLatest] = useState<FirmwareRelease | null>(null);
  const [policy, setPolicy] = useState<OtaPolicy | null>(null);
  const [devices, setDevices] = useState<DeviceInventoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actingDeviceId, setActingDeviceId] = useState<string | null>(null);
  const [rollingOut, setRollingOut] = useState<number | null>(null);
  const [broadcasting, setBroadcasting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  // Drag-and-drop publish.
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pVersion, setPVersion] = useState("");
  const [pNotes, setPNotes] = useState("");
  const [pMandatory, setPMandatory] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Advanced: publish from an already-hosted GitHub Release asset — the
  // OTA path that reaches releases CI can't drop onto this host directly.
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [gVersion, setGVersion] = useState("");
  const [gAssetUrl, setGAssetUrl] = useState("");
  const [gSha, setGSha] = useState("");
  const [gSize, setGSize] = useState("");
  const [gFilename, setGFilename] = useState("");
  const [gNotes, setGNotes] = useState("");
  const [gMandatory, setGMandatory] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [releasesRes, latestRes, policyRes, invRes] = await Promise.allSettled([
        api.listFirmware(CHANNEL),
        api.getLatestFirmware(CHANNEL),
        api.getOtaPolicy(CHANNEL),
        api.listDeviceInventory(),
      ]);
      if (releasesRes.status === "fulfilled") setReleases(releasesRes.value);
      else if (releasesRes.reason instanceof Error) setError(releasesRes.reason.message);

      if (latestRes.status === "fulfilled") setLatest(latestRes.value);
      else setLatest(null); // 404 = no release published yet, not an error

      if (policyRes.status === "fulfilled") setPolicy(policyRes.value);
      else setPolicy(null);

      // Telemetry-backed inventory is best-effort — a dead upstream degrades
      // per-release device counts to 0 rather than blanking the page.
      if (invRes.status === "fulfilled") setDevices(invRes.value.devices ?? []);
      else setDevices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const id = window.setInterval(load, REFRESH_MS);
    return () => window.clearInterval(id);
  }, [load]);

  // The version currently being served: an enabled pin overrides the
  // catalog max, exactly like GetLatestFirmwareHandler resolves it.
  const rolledOutCode = policy?.enabled && policy.target_version_code != null
    ? policy.target_version_code
    : (releases[0]?.version_code ?? latest?.version_code ?? null);

  const sortedReleases = useMemo(
    () => [...releases].sort((a, b) => b.version_code - a.version_code),
    [releases]
  );

  const deviceCountByVersionCode = useMemo(() => {
    const m = new Map<number, number>();
    for (const d of devices) {
      const code = parseVersionCode(d.app_version);
      if (code == null) continue;
      m.set(code, (m.get(code) ?? 0) + 1);
    }
    return m;
  }, [devices]);

  const summary = useMemo(() => {
    let online = 0, behind = 0, current = 0, unknown = 0;
    for (const d of devices) {
      if (d.status?.toUpperCase() === "ONLINE") online++;
      const s = deviceVersionStatus(d, latest);
      if (s.label === "behind") behind++;
      else if (s.label === "up to date") current++;
      else unknown++;
    }
    return { online, behind, current, unknown, total: devices.length };
  }, [devices, latest]);

  const onForceOne = useCallback(async (deviceId: string) => {
    setActingDeviceId(deviceId);
    try {
      const res = await api.forceUpdateDevice(deviceId);
      setToast(`force_update sent to ${deviceId} (cmd ${res.cmd_id.slice(0, 8)}…)`);
    } catch (err) {
      setToast(err instanceof Error ? `Error: ${err.message}` : "force_update failed");
    } finally {
      setActingDeviceId(null);
    }
  }, []);

  const onForceAll = useCallback(async () => {
    if (!window.confirm("Send force_update to every ONLINE tablet in the fleet?")) return;
    setBroadcasting(true);
    try {
      const res: DiagnosticsBroadcastResponse = await api.broadcastForceUpdate(true);
      setToast(`Broadcast: ${res.published} published, ${res.skipped} skipped, ${res.failed} failed (of ${res.total} total)`);
    } catch (err) {
      setToast(err instanceof Error ? `Error: ${err.message}` : "broadcast failed");
    } finally {
      setBroadcasting(false);
    }
  }, []);

  const rollOut = useCallback(async (release: FirmwareRelease) => {
    if (!window.confirm(`Roll out ${release.version_name} as the live version for ${CHANNEL}?`)) return;
    setRollingOut(release.version_code);
    try {
      await api.updateOtaPolicy(CHANNEL, { target_version_code: release.version_code, enabled: true });
      setToast(`${release.version_name} is now rolling out to ${CHANNEL}`);
      await load();
    } catch (err) {
      setToast(err instanceof Error ? `Error: ${err.message}` : "roll out failed");
    } finally {
      setRollingOut(null);
    }
  }, [load]);

  function pickFile(f: File | null) {
    if (!f) return;
    if (!/\.apk$/i.test(f.name)) {
      setToast("Only signed .apk builds are accepted");
      return;
    }
    setPendingFile(f);
    // Guess a version from the filename (e.g. driver-console-1.0.26.apk) so
    // the operator usually just has to confirm rather than retype it.
    const m = f.name.match(/(\d+\.\d+\.\d+)/);
    if (m) setPVersion(m[1]);
  }

  const onUpload = useCallback(async () => {
    if (!pendingFile) return;
    const version = pVersion.trim();
    if (!version) {
      setToast("Version (MAJOR.MINOR.PATCH) is required");
      return;
    }
    setUploading(true);
    try {
      const rel = await api.uploadFirmware(CHANNEL, pendingFile, {
        version_name: version,
        release_notes: pNotes.trim() || undefined,
        mandatory: pMandatory,
      });
      setToast(`Published ${rel.version_name} (${rel.version_code})`);
      setPendingFile(null); setPVersion(""); setPNotes(""); setPMandatory(false);
      await load();
    } catch (err) {
      setToast(err instanceof Error ? `Error: ${err.message}` : "upload failed");
    } finally {
      setUploading(false);
    }
  }, [pendingFile, pVersion, pNotes, pMandatory, load]);

  const onPublishFromGithub = useCallback(async () => {
    const version = gVersion.trim();
    const filename = gFilename.trim() || (version ? `driver-console-${version}.apk` : "");
    const size = Number(gSize.trim());
    if (!version || !gAssetUrl.trim() || !gSha.trim() || !Number.isFinite(size) || size <= 0) {
      setToast("version, asset URL, sha256 and a positive size are required");
      return;
    }
    setPublishing(true);
    try {
      const rel = await api.publishFirmware(CHANNEL, {
        version_name: version,
        filename,
        asset_url: gAssetUrl.trim(),
        sha256_hex: gSha.trim().toLowerCase().replace(/^sha256:/, ""),
        size_bytes: size,
        release_notes: gNotes.trim() || undefined,
        mandatory: gMandatory,
      });
      setToast(`Published ${rel.version_name} (${rel.version_code})`);
      setGVersion(""); setGAssetUrl(""); setGSha(""); setGSize(""); setGFilename(""); setGNotes(""); setGMandatory(false);
      await load();
    } catch (err) {
      setToast(err instanceof Error ? `Error: ${err.message}` : "publish failed");
    } finally {
      setPublishing(false);
    }
  }, [gVersion, gAssetUrl, gSha, gSize, gFilename, gNotes, gMandatory, load]);

  const sortedDevices = useMemo(() => {
    const order = (d: DeviceInventoryEntry): number => {
      const s = deviceVersionStatus(d, latest).label;
      if (s === "behind") return 0;
      if (s === "no code" || s === "unknown" || s === "no catalog") return 1;
      if (s === "up to date") return 2;
      return 3;
    };
    return [...devices].sort((a, b) => {
      const oa = order(a), ob = order(b);
      if (oa !== ob) return oa - ob;
      return (b.last_heartbeat_at ?? 0) - (a.last_heartbeat_at ?? 0);
    });
  }, [devices, latest]);

  return (
    <div className="page firmware-page">
      <div className="firmware-header">
        <div>
          <h1>Firmware</h1>
          <p className="secondary-text">Driver console releases and staged rollouts.</p>
        </div>
        <button
          className="btn primary firmware-new-btn"
          type="button"
          onClick={() => fileInputRef.current?.click()}
        >
          + New release
        </button>
      </div>

      {toast && (
        <div className="banner">
          {toast}
          <button type="button" className="btn ghost" style={{ float: "right" }} onClick={() => setToast(null)}>✕</button>
        </div>
      )}
      {error && <div className="banner banner-error">{error}</div>}

      <div className="firmware-hero-grid">
        <section
          className={"card firmware-drop-card" + (dragOver ? " drag-over" : "")}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            pickFile(e.dataTransfer.files?.[0] ?? null);
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".apk"
            style={{ display: "none" }}
            onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
          />
          {!pendingFile ? (
            <>
              <span className="firmware-drop-icon">⬆</span>
              <div className="firmware-drop-title">Drop firmware package</div>
              <div className="secondary-text firmware-drop-hint">.apk · signed builds only</div>
              <button className="btn primary firmware-browse-btn" type="button" onClick={() => fileInputRef.current?.click()}>
                Browse files
              </button>
            </>
          ) : (
            <div className="firmware-pending">
              <div className="firmware-pending-name mono">{pendingFile.name}</div>
              <div className="secondary-text">{fmtBytes(pendingFile.size)}</div>
              <label className="firmware-field">
                <span>Version</span>
                <input value={pVersion} onChange={(e) => setPVersion(e.target.value)} placeholder="1.0.26" />
              </label>
              <label className="firmware-field">
                <span>Release notes</span>
                <input value={pNotes} onChange={(e) => setPNotes(e.target.value)} placeholder="optional" />
              </label>
              <label className="firmware-mandatory-row">
                <input type="checkbox" checked={pMandatory} onChange={(e) => setPMandatory(e.target.checked)} />
                Mandatory update
              </label>
              <div className="firmware-pending-actions">
                <button className="btn primary" type="button" disabled={uploading} onClick={onUpload}>
                  {uploading ? "Publishing…" : "Publish release"}
                </button>
                <button className="btn ghost" type="button" disabled={uploading} onClick={() => { setPendingFile(null); setPVersion(""); setPNotes(""); setPMandatory(false); }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </section>

        <section className="card firmware-releases-card">
          <h3>Releases</h3>
          <div className="firmware-release-list">
            {sortedReleases.length === 0 && (
              <div className="empty">{loading ? "Loading…" : "No releases published yet."}</div>
            )}
            {sortedReleases.map((r) => {
              const tier = releaseTier(r, rolledOutCode);
              const count = deviceCountByVersionCode.get(r.version_code) ?? 0;
              return (
                <div className="firmware-release-row" key={r.version_code}>
                  <div className="firmware-release-main">
                    <div className="firmware-release-title">
                      <span className="mono">v{r.version_name}</span>
                      <span className={`firmware-tier-pill ${tier}`}>{tier[0].toUpperCase() + tier.slice(1)}</span>
                    </div>
                    <div className="secondary-text firmware-release-notes">
                      {r.release_notes || "—"}
                    </div>
                  </div>
                  <span className="secondary-text firmware-device-count mono">{count} devices</span>
                  <button
                    className="btn firmware-rollout-btn"
                    type="button"
                    disabled={rollingOut === r.version_code || tier === "stable"}
                    onClick={() => rollOut(r)}
                    title={tier === "stable" ? "Already the live version" : "Pin this version as live"}
                  >
                    {rollingOut === r.version_code ? "…" : tier === "stable" ? "Live" : "Roll out"}
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      <details className="card firmware-advanced" style={{ padding: 0 }} open={showAdvanced} onToggle={(e) => setShowAdvanced((e.target as HTMLDetailsElement).open)}>
        <summary>Advanced: device firmware status, GitHub publish, fleet broadcast</summary>

        <div className="firmware-advanced-body">
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <span className="pill sev-info">Total: {summary.total}</span>
            <span className="pill sev-ok">Online: {summary.online}</span>
            <span className={summary.behind > 0 ? "pill sev-warning" : "pill sev-info"}>Behind: {summary.behind}</span>
            <span className="pill sev-info">Current: {summary.current}</span>
            <button type="button" className="btn crit" onClick={onForceAll} disabled={broadcasting || summary.online === 0}>
              {broadcasting ? "Sending…" : "Force update all (online)"}
            </button>
            <button type="button" className="btn ghost" onClick={() => void load()} disabled={loading}>Refresh</button>
          </div>

          <div style={{ display: "grid", gap: 8, marginTop: 16 }}>
            <strong>Publish from a GitHub Release asset</strong>
            <span className="secondary-text" style={{ fontSize: 12 }}>
              Register a GitHub Release APK with FMS. From the release's asset, copy the
              API asset URL, the sha256 digest, and the size in bytes. version_code is
              derived from the version. FMS fetches + verifies the APK, then serves it as latest.
            </span>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <input placeholder="Version (e.g. 1.0.11)" value={gVersion}
                onChange={(e) => setGVersion(e.target.value)} style={{ maxWidth: 150 }} />
              <input placeholder="APK filename (default driver-console-<v>.apk)" value={gFilename}
                onChange={(e) => setGFilename(e.target.value)} style={{ minWidth: 280, flex: 1 }} />
            </div>
            <input placeholder="Asset URL (https://api.github.com/repos/.../releases/assets/<id>)"
              value={gAssetUrl} onChange={(e) => setGAssetUrl(e.target.value)} />
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <input placeholder="sha256 (64 hex)" value={gSha}
                onChange={(e) => setGSha(e.target.value)}
                style={{ minWidth: 320, flex: 1, fontFamily: "var(--mono, monospace)" }} />
              <input placeholder="size (bytes)" value={gSize}
                onChange={(e) => setGSize(e.target.value.replace(/[^0-9]/g, ""))} style={{ maxWidth: 130 }} />
            </div>
            <input placeholder="Release notes (optional)" value={gNotes}
              onChange={(e) => setGNotes(e.target.value)} />
            <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13 }}>
              <input type="checkbox" checked={gMandatory} onChange={(e) => setGMandatory(e.target.checked)} /> Mandatory
            </label>
            <div>
              <button type="button" className="btn primary" onClick={() => void onPublishFromGithub()} disabled={publishing}>
                {publishing ? "Publishing…" : "Publish"}
              </button>
            </div>
          </div>

          <table className="data-table" style={{ marginTop: 16 }}>
            <thead>
              <tr>
                <th>Status</th><th>Device ID</th><th>App version</th><th>vs latest</th>
                <th>Model</th><th>OS</th><th>IP / MAC</th><th>Last heartbeat</th><th></th>
              </tr>
            </thead>
            <tbody>
              {!loading && devices.length === 0 && (
                <tr><td colSpan={9} className="empty">No devices reported yet.</td></tr>
              )}
              {sortedDevices.map((d) => {
                const vs = deviceVersionStatus(d, latest);
                const hb = fmtHeartbeat(d.last_heartbeat_at);
                const online = d.status?.toUpperCase() === "ONLINE";
                return (
                  <tr key={d.device_id}>
                    <td><span className={`pill ${online ? "sev-ok" : "sev-warning"}`}>{online ? "ONLINE" : (d.status || "OFFLINE")}</span></td>
                    <td className="mono" style={{ fontSize: 12 }}>{d.device_id}</td>
                    <td className="mono">{d.app_version || "—"}</td>
                    <td><span className={`pill sev-${vs.pill === "ok" ? "ok" : vs.pill === "warn" ? "warning" : vs.pill === "crit" ? "crit" : "info"}`}>{vs.label}</span></td>
                    <td>{d.device_model || "—"}</td>
                    <td>{d.os_version || "—"}</td>
                    <td className="mono" style={{ fontSize: 11 }}>{d.ip_address || "—"}<br />{d.mac_address || "—"}</td>
                    <td><span className={`pill sev-${hb.tone === "ok" ? "ok" : hb.tone === "warn" ? "warning" : hb.tone === "crit" ? "crit" : "info"}`}>{hb.text}</span></td>
                    <td>
                      <button type="button" className="btn" onClick={() => void onForceOne(d.device_id)}
                        disabled={!online || actingDeviceId === d.device_id} title={online ? "Send force_update over MQTT" : "Device offline"}>
                        {actingDeviceId === d.device_id ? "Sending…" : "Force update"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}
